import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Container, EmptyContent, Text } from "rothzerg-ui";
import { BreadcrumbNav } from "@/components/catalog/BreadcrumbNav";
import { CategoryToolbar } from "@/components/catalog/CategoryToolbar";
import { FeedbackBanner } from "@/components/catalog/FeedbackBanner";
import { FilterSheet } from "@/components/catalog/FilterSheet";
import { OnePassPromoCard } from "@/components/catalog/OnePassPromoCard";
import { ProductGrid } from "@/components/catalog/ProductGrid";
import { YouMightAlsoLike } from "@/components/catalog/YouMightAlsoLike";
import { getCategoryMeta } from "@/data/categories";
import {
  getProductsByCategory,
  paginateProducts,
  sortProducts,
} from "@/data/selectors";
import type { SortOption } from "@/types/catalog";

const PAGE_SIZE = 12;

export function CategoryPage() {
  const { categorySlug = "", brandSlug } = useParams();
  const [sort, setSort] = useState<SortOption>("relevance");
  const [includeMarketplace, setIncludeMarketplace] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [descExpanded, setDescExpanded] = useState(false);

  const meta = getCategoryMeta(categorySlug, brandSlug);

  const allProducts = useMemo(() => {
    const items = getProductsByCategory(categorySlug, brandSlug, {
      includeMarketplace,
    });
    return sortProducts(items, sort);
  }, [categorySlug, brandSlug, includeMarketplace, sort]);

  const { items, rangeStart, rangeEnd, totalCount } = paginateProducts(
    allProducts,
    page,
    PAGE_SIZE,
  );

  if (!meta) {
    return (
      <Container>
        <EmptyContent
          title="Category not found"
          description="We couldn't find that category."
          actions={<a href="/">Back to home</a>}
        />
      </Container>
    );
  }

  const shortDesc =
    meta.description.length > 160 && !descExpanded
      ? `${meta.description.slice(0, 160)}...`
      : meta.description;

  return (
    <div id="category-page" className="kmart-page">
      <Container>
        <BreadcrumbNav items={meta.breadcrumbs} />

        <header className="kmart-category-header">
          <Text id="category-title" variant="h2" as="h1">
            {meta.title}
          </Text>
          <Text variant="body">
            {shortDesc}{" "}
            {meta.description.length > 160 && (
              <button
                type="button"
                id="category-read-more"
                onClick={() => setDescExpanded((v) => !v)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--rz-color-info)",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {descExpanded ? "Read less" : "Read more"}
              </button>
            )}
          </Text>
        </header>

        <CategoryToolbar
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          sort={sort}
          onSortChange={setSort}
          includeMarketplace={includeMarketplace}
          onMarketplaceChange={setIncludeMarketplace}
          onFilterOpen={() => setFilterOpen(true)}
        />

        <ProductGrid
          products={items}
          promoSlot={<OnePassPromoCard />}
          promoIndex={3}
        />

        {totalCount > PAGE_SIZE && (
          <div style={{ marginTop: "1.5rem", textAlign: "center" }}>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </button>
            <span style={{ margin: "0 1rem" }}>Page {page}</span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={rangeEnd >= totalCount}
            >
              Next
            </button>
          </div>
        )}
      </Container>

      <FeedbackBanner />
      <Container>
        <YouMightAlsoLike categories={meta.relatedCategories ?? []} />
      </Container>

      <FilterSheet open={filterOpen} onOpenChange={setFilterOpen} />
    </div>
  );
}
